import React, { useRef, useEffect, useState } from 'react';
import { CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Instagram,
  Facebook,
  Linkedin,
  Video,
  MoreVertical,
  Send,
  Ban,
  Tag,
  User,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  CheckCheck,
  Check,
  Clock,
  AlertCircle
} from 'lucide-react';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import {
  SocialContactWithDetails,
  SocialMessage,
  SocialPlatform,
  SocialMessageStatus,
  PLATFORM_INFO
} from '@/types/social';
import { cn } from '@/lib/core/utils';
import { MessageInput } from './MessageInput';

interface ConversationThreadProps {
  contact: SocialContactWithDetails;
  messages: SocialMessage[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onSendMessage: (content: string) => Promise<void>;
  onBlockContact: () => void;
}

export const ConversationThread: React.FC<ConversationThreadProps> = ({
  contact,
  messages,
  isLoading,
  hasMore,
  onLoadMore,
  onSendMessage,
  onBlockContact
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const getPlatformIcon = (platform: SocialPlatform) => {
    switch (platform) {
      case 'instagram': return Instagram;
      case 'facebook': return Facebook;
      case 'linkedin': return Linkedin;
      case 'tiktok': return Video;
    }
  };

  const Icon = getPlatformIcon(contact.platform);
  const platformInfo = PLATFORM_INFO[contact.platform];

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (isAtBottom && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isAtBottom]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    setIsAtBottom(atBottom);

    // Load more when scrolled to top
    if (element.scrollTop < 100 && hasMore && !isLoading) {
      onLoadMore();
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = new Date(message.sent_at);
    const dateKey = format(date, 'yyyy-MM-dd');

    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(message);

    return groups;
  }, {} as Record<string, SocialMessage[]>);

  const formatDateHeader = (dateKey: string) => {
    const date = new Date(dateKey);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'EEEE, MMMM d, yyyy');
  };

  const getStatusIcon = (status: SocialMessageStatus) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-3 w-3 text-muted-foreground" />;
      case 'sent':
        return <Check className="h-3 w-3 text-muted-foreground" />;
      case 'delivered':
        return <CheckCheck className="h-3 w-3 text-muted-foreground" />;
      case 'read':
        return <CheckCheck className="h-3 w-3 text-blue-500" />;
      case 'failed':
        return <AlertCircle className="h-3 w-3 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Header */}
      <CardHeader className="flex-shrink-0 border-b py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar className="h-10 w-10">
                <AvatarImage src={contact.profile_picture_url || undefined} />
                <AvatarFallback>
                  {(contact.display_name || contact.username || '?').slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div
                className="absolute -bottom-1 -right-1 rounded-full p-0.5"
                style={{ backgroundColor: platformInfo.bgColor }}
              >
                <Icon className="h-3 w-3" style={{ color: platformInfo.color }} />
              </div>
            </div>
            <div>
              <h3 className="font-semibold">
                {contact.display_name || contact.username || 'Unknown'}
              </h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {contact.username && (
                  <span>@{contact.username}</span>
                )}
                {contact.is_follower && (
                  <Badge className="text-xs">Follower</Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* View on Platform */}
            {contact.username && (
              <Button variant="outline" size="sm" asChild>
                <a
                  href={
                    contact.platform === 'instagram'
                      ? `https://instagram.com/${contact.username}`
                      : contact.platform === 'facebook'
                      ? `https://facebook.com/${contact.platform_user_id}`
                      : '#'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  View Profile
                </a>
              </Button>
            )}

            {/* More Actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>
                  <Tag className="h-4 w-4 mr-2" />
                  Manage Tags
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <User className="h-4 w-4 mr-2" />
                  View Contact Details
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-red-600" onClick={onBlockContact}>
                  <Ban className="h-4 w-4 mr-2" />
                  Block Contact
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>

      {/* Messages */}
      <CardContent className="flex-1 overflow-hidden p-0">
        <ScrollArea className="h-full" ref={scrollRef} onScroll={handleScroll}>
          <div className="p-4 space-y-4">
            {/* Load More Button */}
            {hasMore && (
              <div className="flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onLoadMore}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Load older messages
                </Button>
              </div>
            )}

            {isLoading && messages.length === 0 ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="rounded-full bg-muted p-3 mb-3">
                  <Send className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">
                  No messages yet. Start the conversation!
                </p>
              </div>
            ) : (
              Object.entries(groupedMessages).map(([dateKey, dayMessages]) => (
                <div key={dateKey}>
                  {/* Date Header */}
                  <div className="flex items-center justify-center my-4">
                    <div className="bg-muted px-3 py-1 rounded-full text-xs text-muted-foreground">
                      {formatDateHeader(dateKey)}
                    </div>
                  </div>

                  {/* Messages for this day */}
                  <div className="space-y-2">
                    {dayMessages.map((message) => (
                      <MessageBubble
                        key={message.id}
                        message={message}
                        contact={contact}
                        getStatusIcon={getStatusIcon}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </CardContent>

      {/* Message Input */}
      <div className="flex-shrink-0 border-t p-3">
        <MessageInput onSend={onSendMessage} platform={contact.platform} />
      </div>
    </>
  );
};

// Message Bubble Component
interface MessageBubbleProps {
  message: SocialMessage;
  contact: SocialContactWithDetails;
  getStatusIcon: (status: SocialMessageStatus) => React.ReactNode;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  contact,
  getStatusIcon
}) => {
  const isOutbound = message.direction === 'outbound';

  return (
    <div className={cn(
      "flex gap-2",
      isOutbound ? "justify-end" : "justify-start"
    )}>
      {!isOutbound && (
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={contact.profile_picture_url || undefined} />
          <AvatarFallback className="text-xs">
            {(contact.display_name || contact.username || '?').slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      )}

      <div className={cn(
        "max-w-[70%] space-y-1",
        isOutbound && "items-end"
      )}>
        {/* Media */}
        {message.media_url && (
          <div className="rounded-lg overflow-hidden max-w-xs">
            {message.media_mime_type?.startsWith('video') ? (
              <video
                src={message.media_url}
                controls
                className="w-full"
              />
            ) : (
              <img
                src={message.media_url}
                alt="Media"
                className="w-full h-auto"
              />
            )}
          </div>
        )}

        {/* Text Content */}
        {message.content && (
          <div
            className={cn(
              "px-3 py-2 rounded-2xl",
              isOutbound
                ? "bg-primary text-primary-foreground rounded-br-md"
                : "bg-muted rounded-bl-md"
            )}
          >
            <p className="text-sm whitespace-pre-wrap break-words">
              {message.content}
            </p>
          </div>
        )}

        {/* Metadata */}
        <div className={cn(
          "flex items-center gap-1 text-xs text-muted-foreground px-1",
          isOutbound && "justify-end"
        )}>
          <span>{format(new Date(message.sent_at), 'HH:mm')}</span>
          {isOutbound && getStatusIcon(message.status)}
          {message.is_auto_reply && (
            <Badge variant="outline" className="text-[10px] px-1 py-0">
              Auto
            </Badge>
          )}
        </div>
      </div>

      {isOutbound && (
        <div className="w-8 flex-shrink-0" /> // Spacer for alignment
      )}
    </div>
  );
};

export default ConversationThread;
